import { Tooltip } from '@sgme/ui';
import type { DocumentError } from 'apiConnector/document/document';
import classnames from 'classnames';
import { MultiLanguageDocumentError } from 'components/shared/Document/MultiLanguageDocumentError';
import { FormattedMessage } from 'components/shared/Intl';
import { DocumentItem } from 'epic/common-form/Documentation';
import type { IntlKey } from 'locales';
import { DocumentType } from 'models/documents/DocumentType';
import { useMemo } from 'react';
import styled from 'styled-components';
import {
  getDocumentStatusIcon,
  getDocumentTooltipProps,
  isDocumentGenerationFailed,
  isDocumentValidated,
} from 'utils/documents/documentProperties';

export interface BlotterPanelDocumentDownloadButtonWithStatusProps {
  document: DocumentItem;
  e2e?: string;
  onClicked: (language?: string) => void;
  type: DocumentType;
  showStatus?: boolean;
  error?: DocumentError;
}

export const StyledAnchor = styled.a.attrs<{ $linkColor: string }>(({ $linkColor }) => ({
  className: classnames('px-2 text-link d-flex', `link-${$linkColor}`),
}))`
  &[disabled] {
    opacity: unset;
    pointer-events: auto !important;
    &:hover {
      cursor: default !important;
    }
  }
`;

function computeLinkColor(showStatus?: boolean, status?: string) {
  const isGenerationFailed = isDocumentGenerationFailed(status);
  const isValidatedOrFailed = isDocumentValidated(status) || isGenerationFailed;
  if (showStatus && isGenerationFailed) {
    return 'danger';
  } else if (showStatus && status && !isValidatedOrFailed) {
    return 'warning';
  } else {
    return 'secondary';
  }
}

export function BlotterPanelDocumentDownloadButtonWithStatus({
  document,
  e2e,
  showStatus,
  type,
  onClicked,
  error,
}: BlotterPanelDocumentDownloadButtonWithStatusProps) {
  const withTooltipProps = getDocumentTooltipProps(document.status, type, 'blotter');

  const icon = useMemo(() => getDocumentStatusIcon(document.status), [document]);

  const buttonColor = computeLinkColor(showStatus, document.status);

  const isDisabled = showStatus && isDocumentGenerationFailed(document.status);
  const e2eId = `${e2e}-${document.language}`;

  if (error) {
    return (
      <MultiLanguageDocumentError
        error={error}
        e2e={e2eId}
        lang={document.language ?? 'unknown'}
        documentFormat="PDF"
        initOpen={true}
      />
    );
  }

  const iconWithTooltip =
    showStatus && document.status && icon ? (
      <div data-e2e={`${e2eId}-container`}>
        <Tooltip
          position="top"
          contents={<FormattedMessage id={`${withTooltipProps.intlKey}` as IntlKey} />}
          color={withTooltipProps.color}
          data-e2e={e2eId}
        >
          <div data-e2e="tooltip-trigger">{icon && <i className="icon icon-sm ms-2">{icon}</i>}</div>
        </Tooltip>
      </div>
    ) : undefined;

  return (
    <StyledAnchor
      $linkColor={buttonColor}
      data-e2e={e2eId}
      href={isDisabled ? undefined : document.public === undefined || document.public ? document.url : undefined}
      target="_blank"
      onClick={() => onClicked(document.language)}
    >
      <FormattedMessage id={`languageCode.${document.language}` as IntlKey} defaultMessage={document.language} />
      {iconWithTooltip}
    </StyledAnchor>
  );
}

BlotterPanelDocumentDownloadButtonWithStatus.displayName = 'BlotterPanelDocumentDownloadButtonWithStatus';
